'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Socket } from 'socket.io-client'
import { toast } from 'react-toastify'

import {
    getPersonalAlertSound,
    updatePersonalAlertSound,
    type Notification,
} from '@/lib/api/notifications/notification-api'

const SOUND_URL = '/notification.mp3'

/**
 * Intervalo mínimo entre dois toques. Uma rajada de mensagens (várias seguidas do mesmo
 * contato, ou de contatos diferentes ao mesmo tempo) vira UM aviso, não uma metralhadora.
 */
const COOLDOWN_MS = 3000

/**
 * Trava compartilhada entre as abas do mesmo navegador (Web Locks API). Todas recebem o
 * mesmo evento do socket; quem pega a trava toca e a segura durante o intervalo, e as
 * outras desistem. Sem isso, cinco abas abertas tocam cinco vezes.
 */
const LOCK_NAME = 'my-alerts-message-sound'

/**
 * A aba em foco tenta primeiro; as demais esperam um pouco. Tocar na aba que a pessoa
 * está usando é o mais confiável: o navegador só libera áudio em aba que já recebeu um
 * clique, e a que está em uso certamente recebeu.
 */
const BACKGROUND_TAB_DELAY_MS = 250

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Som de mensagem nova, tocado por "Meus Alertas".
 *
 * Toca para o alerta PESSOAL de mensagem (`chat_message`) e mais nenhum: o público é o
 * do próprio alerta (carteira, responsável pelo lead e pelas oportunidades), e quem
 * desligou "Novas mensagens nas conversas" nas preferências não recebe o alerta, logo
 * também não ouve. O alerta agrupado ("7 novas mensagens de Maria") reaproveita o mesmo
 * evento ao ser atualizado, então cada mensagem nova toca, respeitado o intervalo.
 *
 * Substitui o som que existia só na tela de atendimento, que tocava apenas para a
 * conversa aberta e guardava a escolha no navegador.
 */
export function useMessageAlertSound(socket: Socket | null | undefined) {
    const [enabled, setEnabled] = useState(true)
    const [saving, setSaving] = useState(false)
    const enabledRef = useRef(true)
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const lastPlayedAtRef = useRef(0)
    const blockedWarnedRef = useRef(false)

    useEffect(() => {
        enabledRef.current = enabled
    }, [enabled])

    useEffect(() => {
        let cancelled = false
        void getPersonalAlertSound().then(res => {
            if (!cancelled && res.success && res.result) setEnabled(res.result.enabled)
        })
        return () => {
            cancelled = true
        }
    }, [])

    const play = useCallback(async () => {
        if (!audioRef.current) audioRef.current = new Audio(SOUND_URL)
        const audio = audioRef.current
        audio.currentTime = 0
        try {
            await audio.play()
        } catch (error) {
            // Política de autoplay: sem nenhum clique na aba desde que ela abriu, o
            // navegador recusa o som e não avisa ninguém. Avisa uma vez por carga.
            if ((error as DOMException)?.name === 'NotAllowedError' && !blockedWarnedRef.current) {
                blockedWarnedRef.current = true
                toast.info('O navegador bloqueou o som dos alertas. Clique em qualquer lugar da página para liberar.')
            }
        }
    }, [])

    const playOnce = useCallback(() => {
        const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined

        // Sem Web Locks (navegador antigo): ao menos o intervalo dentro desta aba.
        if (!locks) {
            const now = Date.now()
            if (now - lastPlayedAtRef.current < COOLDOWN_MS) return
            lastPlayedAtRef.current = now
            void play()
            return
        }

        const inUse = document.visibilityState === 'visible' && document.hasFocus()
        setTimeout(
            () => {
                void locks.request(LOCK_NAME, { ifAvailable: true }, async lock => {
                    if (!lock) return
                    await play()
                    await sleep(COOLDOWN_MS)
                })
            },
            inUse ? 0 : BACKGROUND_TAB_DELAY_MS,
        )
    }, [play])

    useEffect(() => {
        if (!socket) return
        const onNotification = (notification: Notification) => {
            if (!enabledRef.current) return
            if (notification?.scope !== 'PERSONAL' || notification.alertKind !== 'chat_message') return
            playOnce()
        }
        socket.on('notification:new', onNotification)
        return () => {
            socket.off('notification:new', onNotification)
        }
    }, [socket, playOnce])

    const toggle = useCallback(async () => {
        const previous = enabled
        const next = !previous
        setEnabled(next)
        setSaving(true)
        try {
            const res = await updatePersonalAlertSound(next)
            if (res.success && res.result) {
                setEnabled(res.result.enabled)
                // Amostra ao ligar: a pessoa confere o volume, e o clique libera o áudio
                // desta aba para os próximos alertas.
                if (res.result.enabled) void play()
            } else {
                setEnabled(previous)
                toast.error(res.error || 'Não foi possível salvar a preferência de som.')
            }
        } finally {
            setSaving(false)
        }
    }, [enabled, play])

    return { enabled, saving, toggle }
}
